import { action, makeObservable, observable } from 'mobx';

import { IApiClient } from '../../../../app/data/client';
import { Form, SelectInput, ValueRequiredRule } from '../../../../app/shared/Form';
import Logger from '../../../../app/shared/Logger';
import { SelectPricelistFormTranslation } from '../../../localization/ModuleTranslation';
import type { CustomerPricelistData } from '../../../model';
import {
  AddCustomerPricelistItemsCommand,
  GetCustomerPricelistsQuery,
  PRICELISTS_DEFAULT_SORT,
} from '../../../queries';

/**
 * Collections offered in the picker. It is a select, not a paged list, so it asks for as many as the
 * API will answer with in one request rather than growing a paging control inside a modal.
 */
const PAGE_SIZE = 200;

interface SelectPricelistFormResult {
  isSuccess: boolean;
  errorMessage?: string;
  addedProducts?: number;
  pricelistName?: string;
}

export default class SelectPricelistFormState extends Form {
  @observable.ref public pricelists: CustomerPricelistData[] = [];
  @observable public isLoading = false;

  public readonly pricelist: SelectInput<string>;

  public constructor(
    private readonly client: IApiClient,
    public readonly translation: SelectPricelistFormTranslation,
  ) {
    super();
    makeObservable(this);

    this.pricelist = new SelectInput('').withRule(new ValueRequiredRule());

    this.inputsToValidate.push(this.pricelist);
  }

  public async submit(productModelIds: string[]): Promise<SelectPricelistFormResult> {
    this.validate();

    if (!this.isValid) {
      return { isSuccess: false };
    }

    const pricelistId = this.pricelist.value;
    const totalProducts = this.pricelists.find((p) => p.id === pricelistId)?.totalProducts ?? 0;

    try {
      const response = await this.client.send(new AddCustomerPricelistItemsCommand({ pricelistId, productModelIds }));

      return {
        isSuccess: true,
        // A product already on the collection is not added twice, so what it gained is the
        // difference in its count rather than the number of products sent.
        addedProducts: Math.max(response.pricelist.totalProducts - totalProducts, 0),
        pricelistName: response.pricelist.name,
      };
    } catch (error) {
      Logger.exception('Adding the selected products to a price list failed', error);

      return { isSuccess: false, errorMessage: this.translation.addFailedError };
    }
  }

  public async load(): Promise<void> {
    this.setIsLoading(true);

    try {
      const response = await this.client.send(
        new GetCustomerPricelistsQuery({
          searchPhrase: '',
          sort: PRICELISTS_DEFAULT_SORT,
          skip: 0,
          take: PAGE_SIZE,
        }),
      );

      this.setPricelists(response.pricelists);
    } catch (error) {
      Logger.exception('Loading the price lists failed', error);
      this.setPricelists([]);
    } finally {
      this.setIsLoading(false);
    }
  }

  @action
  public reset(): void {
    this.resetValidation();
  }

  @action
  private setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  /** The first list is picked, so the form opens on a choice that can be submitted. */
  @action
  private setPricelists(pricelists: CustomerPricelistData[]): void {
    this.pricelists = pricelists;
    this.pricelist.setDefaultValue(pricelists.empty() ? '' : pricelists[0].id);
  }
}
